// Carrier-status model (per-individual, per-disease). Projected at render time
// from: existing affected diagnoses, the per-gene clinician `carrierStatus`
// assertion, the terminology gene→disease map, and stored manual obligate
// assertions. See PLAN-carrier-status-pedigree.md.

export const CARRIER_STATUS = Object.freeze({
  affected: 'affected',
  testedCarrier: 'tested_carrier',
  // Carrier known from an external/untracked test or clinical knowledge — no
  // in-TrakGene variant. Distinct from tested (variant-linked) and obligate
  // (pedigree-inferred).
  reportedCarrier: 'reported_carrier',
  obligateCarrier: 'obligate_carrier',
  // Carrier with no stated basis (provenance unspecified) — never silently
  // treated as obligate.
  carrier: 'carrier',
  unaffected: 'unaffected'
});

// Precedence (highest wins): affected > tested_carrier > reported_carrier >
// obligate_carrier > carrier (generic) > unaffected. All carrier kinds render
// the SAME mark; precedence only drives the analysis label.

// The carrier statuses that draw a carrier mark on the node. Use this single
// predicate everywhere a carrier mark is decided, so the four carrier kinds
// never drift apart across call sites.
export const CARRIER_MARK_STATUSES = Object.freeze([
  CARRIER_STATUS.testedCarrier,
  CARRIER_STATUS.reportedCarrier,
  CARRIER_STATUS.obligateCarrier,
  CARRIER_STATUS.carrier
]);

export const isCarrierMark = (status) => CARRIER_MARK_STATUSES.includes(status);

// Gene-level `carrierStatus` values that indicate a (heterozygous) carrier.
// `homozygote-or-compound-heterozygote` is affected-level and reaches the
// pedigree via the affected-diagnosis data, not as a carrier.
//
// SCOPE (TGCLOUD-1712 W4.4). This set decides whether to DRAW a gene→disease
// carrier mark, and it is deliberately NOT the vocabulary for "what does this
// person's genotype result say". That question now has one answer, in
// analysis/carrierStatusVocabulary.js, derived from CARRIER_STATUS_SYMBOL and
// covering all eight values. Do not widen this set to answer it — three
// overlapping sets is the defect that remediation removed.
//
// KNOWN CONSEQUENCE, recorded here on purpose. deriveCarrierStatus gates the
// gene→disease projection on this set, so `homozygote` and `hemizygote` project
// NO carrier status for any disease. A hemizygous male therefore becomes a
// cascade index only once the family's gene is implicated by some other index's
// genotype record (see the INDEX PROMOTION pass in analysis/detectors/cascade.js).
// Closing that fully means either admitting the affected-level zygosities here —
// which changes what the chart draws, and so needs a clinical/design decision —
// or passing the gene→disease map into the analyzer.
export const TESTED_CARRIER_VALUES = Object.freeze(
  new Set(['carrier', 'heterozygote'])
);

// Legend collapses tested + obligate into a single "carrier" presence state.
export const PRESENT_STATE = Object.freeze({
  affected: 'affected',
  carrier: 'carrier'
});
